import React, { useState } from 'react';
import { useImpersonation } from '@/core/auth/ImpersonationContext';
import { useToast } from '@/components/ui';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button, Card, KPICard } from '@/components/ui';
import { useAdminUsers, useAdminUserActions } from './hooks/useUsers';
import { AdminUsersDesktopTable } from './components/UsersDesktopTable';
import { AdminUsersMobileList } from './components/UsersMobileList';
import { useNavigate } from 'react-router-dom';
import { companiesService } from '../companies/services/companies.service';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';

export default function UsersPage() {
  const { profile } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const { toast } = useToast();
  
  const isSuperAdmin = profile?.role_code === 'super_admin';
  const effectiveCompanyId = isSuperAdmin ? selectedCompanyId : profile?.company_id;

  const { data, isLoading } = useAdminUsers({ 
    search, 
    page, 
    company_id: effectiveCompanyId || undefined,
    role_code: selectedRole || undefined
  });

  const { data: companiesRes } = useQuery({
    queryKey: ['admin-companies-list'],
    queryFn: () => companiesService.list()
  });

  const { toggleLock } = useAdminUserActions();

  const users = data?.data || [];
  const totalCount = data?.count || 0;
  const stats = (data as any)?.stats;

  const handleToggleLock = async (userId: string, isActive: boolean) => {
    try {
      await toggleLock({ id: userId, isActive });
      toast.success(isActive ? 'Đã mở khóa người dùng' : 'Đã khóa người dùng');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const handleImpersonate = async (user: any) => {
    try {
      const success = await startImpersonation(user.id);
      if (success) {
        // startImpersonation sẽ tự reload hoặc toast
      }
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <PageLayout
      title="Quản trị Nhân sự"
      description="Quản lý tập trung toàn bộ nhân sự, vai trò và quyền truy cập trên toàn hệ thống."
      breadcrumbs={[
        { label: 'Hệ thống', path: '/admin' },
        { label: 'Nhân sự' }
      ]}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-download-line">Xuất CSV</Button>
          <Button variant="primary" icon="ri-user-add-line" onClick={() => setShowCreateModal(true)}>
            Thêm người dùng
          </Button>
        </div>
      }
      isLoading={isLoading}
      beforeToolbar={
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KPICard title="Tổng người dùng" value={stats?.total || totalCount} icon="ri-group-line" variant="primary" />
          <KPICard title="Đang hoạt động" value={stats?.active || users.filter((u: any) => u.is_active).length} icon="ri-checkbox-circle-line" variant="success" />
          <KPICard title="Bị khóa" value={stats?.locked || users.filter((u: any) => !u.is_active).length} icon="ri-lock-2-line" variant="danger" />
          <KPICard title="Chờ xác minh" value={stats?.pending || 0} icon="ri-time-line" variant="warning" />
        </div>
      }
      onSearchChange={setSearch}
      searchValue={search}
      currentPage={page}
      totalPages={Math.ceil(totalCount / 20)}
      onPageChange={setPage}
      extraLeft={
        <div className="flex flex-wrap gap-2 items-center bg-slate-50/50 p-2 rounded-2xl border border-slate-100/50">
          {isSuperAdmin && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-xl border border-slate-200 shadow-sm">
              <i className="ri-building-line text-slate-400" />
              <select 
                className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent border-none focus:ring-0 p-0 pr-8 cursor-pointer"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
              >
                <option value="">Tất cả tổ chức</option>
                {companiesRes?.data?.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-xl border border-slate-200 shadow-sm">
            <i className="ri-shield-user-line text-slate-400" />
            <select 
              className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent border-none focus:ring-0 p-0 pr-8 cursor-pointer"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">Tất cả vai trò</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="staff">Staff</option>
            </select>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="hidden md:block">
          <AdminUsersDesktopTable 
            users={users} 
            onToggleLock={handleToggleLock}
            onImpersonate={handleImpersonate}
            onEdit={(user) => navigate(`/admin/users/edit/${user.id}`)}
            onViewDetail={(id) => navigate(`./detail/${id}`)}
          />
        </div>
        <div className="md:hidden">
          <AdminUsersMobileList 
            users={users}
            onToggleLock={handleToggleLock}
            onEdit={(user) => navigate(`/admin/users/edit/${user.id}`)}
            onImpersonate={handleImpersonate}
            onViewDetail={(id) => navigate(`./detail/${id}`)}
          />
        </div>
      </div>
    </PageLayout>
  );
}
